import type { MatchEvent } from "@arcade-hockey/shared";
import type { MatchSnapshot } from "@arcade-hockey/sim";
import type { AudioEngine } from "@neoweb/audio";
import type { AudioEventId } from "../audio/event-defs.js";
import type { FxDirector } from "../render/views/FxDirector.js";

/**
 * Translates a stream of MatchEvents into audio and visual-FX calls.
 * Listens for known events; ignores the rest.
 *
 * `enabled` gates dispatch; the replay debug overlay sets it to false during
 * fast-forward playback so 20 ticks of events don't flush 20 ticks of audio
 * into a single render frame.
 */
export class EventDispatcher {
  enabled = true;

  constructor(
    private audio: AudioEngine<AudioEventId>,
    private fx: FxDirector | null = null,
  ) {}

  dispatch(snapshot: MatchSnapshot): void {
    if (!this.enabled) return;
    for (const event of snapshot.events) {
      this.fx?.on(event);
      this.handleOne(event, snapshot);
    }
  }

  private handleOne(event: MatchEvent, snapshot: MatchSnapshot): void {
    switch (event.type) {
      case "ShotReleased": {
        const shooter = snapshot.players.find((p) => p.id === event.from);
        this.audio.post("puck.shot", {
          position: shooter?.pos,
          intensity: 0.7 + Math.min(1, event.speed / 22) * 0.3,
        });
        break;
      }
      case "PuckBoardHit": {
        this.audio.post("puck.boardHit", {
          position: event.pos,
          intensity: Math.min(1, event.speed / 20),
        });
        break;
      }
      case "GoalScored": {
        this.audio.post("match.goal", { intensity: 1 });
        break;
      }
      default:
        return;
    }
  }
}
